import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Users,
  DollarSign,
  TrendingUp,
  UserPlus,
  CreditCard,
  Crown,
  Shield,
  Ban,
  CheckCircle,
  Clock,
  AlertTriangle,
  ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  bronze: "text-orange-600 bg-orange-500/20",
  silver: "text-gray-400 bg-gray-500/20",
  gold: "text-yellow-500 bg-yellow-500/20",
  platinum: "text-purple-400 bg-purple-500/20"
};

const STATUS_COLORS = {
  active: "text-green-500 bg-green-500/20",
  suspended: "text-yellow-500 bg-yellow-500/20",
  banned: "text-red-500 bg-red-500/20"
};

export default function AdminAffiliates() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);

  useEffect(() => {
    // Set admin token from localStorage (using correct key: admin_token)
    const adminToken = localStorage.getItem("admin_token");
    if (!adminToken) {
      // Redirect to admin login if no token
      window.location.href = "/admin";
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${adminToken}`;
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const response = await axios.get(`${API}/admin/affiliates`);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load affiliates");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (userId, newStatus) => {
    try {
      await axios.put(`${API}/admin/affiliate/${userId}/status?status=${newStatus}`);
      toast.success(`Affiliate status updated to ${newStatus}`);
      fetchAffiliates();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Users className="h-12 w-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-primary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/admin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Admin
            </Link>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-green-500" />
              <span className="font-bold tracking-tight">AFFILIATE MANAGEMENT</span>
            </div>
            <Link to="/admin/payouts">
              <Button variant="outline" size="sm">
                <DollarSign className="h-4 w-4 mr-2" /> Payouts
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="bg-card border-green-500/30">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 text-green-500 mx-auto mb-2" />
                <p className="text-3xl font-black">{data?.summary?.total_affiliates || 0}</p>
                <p className="text-xs text-muted-foreground uppercase">Total Affiliates</p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-card border-blue-500/30">
              <CardContent className="p-4 text-center">
                <UserPlus className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                <p className="text-3xl font-black">{data?.summary?.total_signups_generated || 0}</p>
                <p className="text-xs text-muted-foreground uppercase">Signups Generated</p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-card border-yellow-500/30">
              <CardContent className="p-4 text-center">
                <TrendingUp className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                <p className="text-3xl font-black">${(data?.summary?.total_earnings_generated || 0).toFixed(0)}</p>
                <p className="text-xs text-muted-foreground uppercase">Total Earnings</p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-card border-red-500/30">
              <CardContent className="p-4 text-center">
                <DollarSign className="h-6 w-6 text-red-500 mx-auto mb-2" />
                <p className="text-3xl font-black">${(data?.summary?.total_pending_payouts || 0).toFixed(2)}</p>
                <p className="text-xs text-muted-foreground uppercase">Pending Payouts</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tier Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                <Crown className="h-4 w-4 text-yellow-500" /> Tier Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {["bronze", "silver", "gold", "platinum"].map((tier) => (
                  <div key={tier} className={`p-3 rounded-lg ${TIER_COLORS[tier]} text-center`}>
                    <p className="text-2xl font-black">{data?.summary?.tier_breakdown?.[tier] || 0}</p>
                    <p className="text-xs uppercase font-bold">{tier}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Affiliates Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-wider">All Affiliates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 text-xs uppercase text-muted-foreground">Affiliate</th>
                      <th className="text-center py-3 px-2 text-xs uppercase text-muted-foreground">Tier</th>
                      <th className="text-center py-3 px-2 text-xs uppercase text-muted-foreground">Status</th>
                      <th className="text-right py-3 px-2 text-xs uppercase text-muted-foreground">Clicks</th>
                      <th className="text-right py-3 px-2 text-xs uppercase text-muted-foreground">Signups</th>
                      <th className="text-right py-3 px-2 text-xs uppercase text-muted-foreground">Earnings</th>
                      <th className="text-right py-3 px-2 text-xs uppercase text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.affiliates?.map((affiliate, index) => (
                      <tr key={affiliate.id} className="border-b border-border/50 hover:bg-secondary/30">
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-bold">{affiliate.name}</p>
                            <p className="text-xs text-muted-foreground">{affiliate.email}</p>
                            <p className="text-xs text-primary font-mono">{affiliate.code}</p>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${TIER_COLORS[affiliate.tier || 'bronze']}`}>
                            {affiliate.tier || 'bronze'}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${STATUS_COLORS[affiliate.status || 'active']}`}>
                            {affiliate.status || 'active'}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right font-mono">
                          {affiliate.stats?.clicks || 0}
                        </td>
                        <td className="py-3 px-2 text-right font-mono">
                          {affiliate.stats?.signups || 0}
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-green-500">
                          ${(affiliate.stats?.total_earnings || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <div className="flex gap-1 justify-end">
                            {affiliate.status !== 'active' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => updateStatus(affiliate.user_id, 'active')}
                                className="text-green-500 hover:text-green-400 h-8 w-8 p-0"
                                title="Activate"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            {affiliate.status !== 'suspended' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => updateStatus(affiliate.user_id, 'suspended')}
                                className="text-yellow-500 hover:text-yellow-400 h-8 w-8 p-0"
                                title="Suspend"
                              >
                                <Shield className="h-4 w-4" />
                              </Button>
                            )}
                            {affiliate.status !== 'banned' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => updateStatus(affiliate.user_id, 'banned')}
                                className="text-red-500 hover:text-red-400 h-8 w-8 p-0"
                                title="Ban"
                              >
                                <Ban className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {(!data?.affiliates || data.affiliates.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    No affiliates yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
